import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Building2, Users, Home, Calendar, Plus, ArrowLeft, UserCheck, UserX, Ban, AlertTriangle, Eye, EyeOff, Pencil, Trash2, Copy, Check, LogIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGIN_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SuperAdminDashboard = ({ onSwitchToTenantApp }) => {
  const navigate = useNavigate();
  const { user, isSuperAdmin, logout } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showOnlyInactive, setShowOnlyInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ company_name: "", admin_email: "", admin_password: "", admin_full_name: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingExpiration, setEditingExpiration] = useState(null);
  const [expirationInput, setExpirationInput] = useState("");
  const [editingTenant, setEditingTenant] = useState(null);
  const [editPassword, setEditPassword] = useState("");
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (isSuperAdmin()) loadTenants();
     
  }, [user, includeInactive]);

  const loadTenants = async () => {
    setLoading(true);
    try {
      // Assurer que le token est présent dans les headers avant l'appel
      // (évite race condition si l'appel arrive avant que AuthContext ait injecté le token)
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Session expirée, veuillez vous reconnecter");
        return;
      }
      const res = await axios.get(`${API}/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
        params: includeInactive ? { include_inactive: true } : {},
      });
      setTenants(res.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      console.error("Erreur chargement tenants:", status, detail, err);
      if (status === 401 || status === 403) {
        toast.error(`Accès refusé (${status}) : ${detail || 'permissions insuffisantes'}`);
      } else if (!err.response) {
        toast.error("Erreur réseau : impossible de joindre le serveur");
      } else {
        toast.error(`Erreur chargement des tenants (${status || 'inconnu'})`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tenants`, form);
      setCreatedCredentials({
        company_name: form.company_name,
        email: form.admin_email,
        password: form.admin_password
      });
      setShowCreate(false);
      setForm({ company_name: "", admin_email: "", admin_password: "", admin_full_name: "" });
      setShowPassword(false);
      await loadTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la creation");
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleSuspend = async (tenant) => {
    const action = tenant.suspended ? "reactiver" : "suspendre";
    if (!tenant.suspended && !window.confirm(`Suspendre "${tenant.company_name}" ? Tous les utilisateurs verront un ecran de blocage.`)) return;
    try {
      await axios.post(`${API}/tenants/${tenant.id}/suspend`);
      toast.success(`${tenant.company_name} ${tenant.suspended ? "reactive" : "suspendu"}`);
      await loadTenants();
    } catch (err) {
      toast.error("Erreur lors de la modification");
    }
  };

  const toggleTenant = async (tenant) => {
    try {
      if (tenant.active) {
        await axios.delete(`${API}/tenants/${tenant.id}`);
        toast.success(`${tenant.company_name} desactive`);
      } else {
        // Utiliser l'endpoint /reactivate qui réinscrit aussi dans tenant_user_index
        await axios.post(`${API}/tenants/${tenant.id}/reactivate`);
        toast.success(`${tenant.company_name} reactive`);
      }
      await loadTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la modification");
    }
  };

  const saveExpiration = async (tenantId) => {
    try {
      await axios.put(`${API}/tenants/${tenantId}/expiration`, { expiration_date: expirationInput || null });
      toast.success(expirationInput ? "Date d'expiration definie" : "Date d'expiration supprimee");
      setEditingExpiration(null);
      setExpirationInput("");
      await loadTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const openTenantUsers = async (tenant) => {
    setSelectedTenant(tenant);
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/tenants/${tenant.id}/users`);
      setTenantUsers(res.data);
    } catch (err) {
      toast.error("Erreur chargement des utilisateurs");
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUser = async (tenantUser) => {
    try {
      const newActive = !tenantUser.active;
      await axios.put(`${API}/tenants/${selectedTenant.id}/users/${tenantUser.id}`, { active: newActive });
      toast.success(`${tenantUser.full_name || tenantUser.email} ${newActive ? "active" : "desactive"}`);
      const res = await axios.get(`${API}/tenants/${selectedTenant.id}/users`);
      setTenantUsers(res.data);
      await loadTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteTenant = async (tenant) => {
    if (!window.confirm(`Désactiver "${tenant.company_name}" ?\nLe tenant sera masqué de la liste des conciergeries actives mais ses données sont conservées. Vous pourrez le réactiver à tout moment depuis la section "Désactivés".`)) return;
    try {
      await axios.delete(`${API}/tenants/${tenant.id}`);
      // Mettre à jour localement : marquer inactif plutôt que retirer
      setTenants(prev => prev.map(t => t.id === tenant.id ? { ...t, active: false } : t));
      toast.success(`${tenant.company_name} désactivé`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la désactivation");
    }
  };

  const openEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setEditPassword("");
    setShowEditPassword(false);
    setPasswordSaved(false);
  };

  const saveAdminPassword = async () => {
    if (!editPassword || editPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caracteres");
      return;
    }
    try {
      await axios.put(`${API}/tenants/${editingTenant.id}/admin-password`, { new_password: editPassword });
      toast.success("Mot de passe mis a jour");
      setPasswordSaved(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Fonction pour basculer vers l'espace d'un tenant (impersonation)
  const switchToTenant = (tenant) => {
    // Sauvegarder l'info qu'on vient du super admin pour pouvoir revenir
    localStorage.setItem("superAdminMode", "true");
    localStorage.setItem("originalUser", JSON.stringify(user));
    localStorage.setItem("viewingTenantId", tenant.id);
    localStorage.setItem("viewingTenantName", tenant.company_name);
    
    toast.success(`Basculement vers ${tenant.company_name}`);
    navigate("/");
  };

  if (!isSuperAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center max-w-md">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acces restreint</h2>
          <p className="text-gray-600">Cette page est reservee au super-administrateur.</p>
        </div>
      </div>
    );
  }

  const activeTenants = tenants.filter(t => t.active);
  const totalStats = activeTenants.reduce(
    (acc, t) => ({
      users: acc.users + (t.stats?.users || 0),
      housings: acc.housings + (t.stats?.housings || 0),
      missions: acc.missions + (t.stats?.missions || 0),
    }),
    { users: 0, housings: 0, missions: 0 }
  );
  const alertCount = tenants.filter(t => t.active && !t.suspended && t.days_until_expiration !== null && t.days_until_expiration <= 7 && t.days_until_expiration >= 0).length;
  const suspendedCount = tenants.filter(t => t.suspended).length;
  const inactiveCount = tenants.filter(t => !t.active).length;
  const filteredTenants = showOnlyInactive ? tenants.filter(t => !t.active) : tenants;

  // --- User detail view ---
  if (selectedTenant) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="tenant-users-view">
        <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setSelectedTenant(null)} className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg" data-testid="back-to-tenants-btn">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">{selectedTenant.company_name}</h1>
                <p className="text-xs text-gray-500">Gestion des utilisateurs</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onSwitchToTenantApp && (
                <button onClick={onSwitchToTenantApp} className="px-3 py-1.5 text-sm bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors font-medium" data-testid="switch-to-tenant-app-btn-detail">Mon espace M&M →</button>
              )}
              <button onClick={logout} className="px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">Deconnexion</button>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          {loadingUsers ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
          ) : (
            <div className="space-y-3">
              {tenantUsers.map((u) => (
                <div key={u.id} className={`bg-white rounded-xl border p-4 ${u.active ? "border-gray-200" : "border-red-200 bg-red-50/30 opacity-75"}`} data-testid={`tenant-user-${u.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${u.active ? (u.role === 'admin' ? 'bg-red-600' : 'bg-blue-500') : 'bg-gray-400'}`}>
                        {(u.full_name || u.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-900">{u.full_name || "Sans nom"}</p>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${u.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                            {u.role === 'admin' ? 'Admin' : 'Utilisateur'}
                          </span>
                          {!u.active && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Desactive</span>}
                        </div>
                        <p className="text-sm text-gray-500">{u.email}</p>
                      </div>
                    </div>
                    <button onClick={() => toggleUser(u)}
                      className={`p-2 rounded-lg transition-colors ${u.active ? "text-gray-400 hover:text-red-600 hover:bg-red-50" : "text-gray-400 hover:text-green-600 hover:bg-green-50"}`}
                      title={u.active ? "Desactiver" : "Reactiver"} data-testid={`toggle-user-${u.id}`}>
                      {u.active ? <UserX className="h-5 w-5" /> : <UserCheck className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              ))}
              {tenantUsers.length === 0 && <div className="text-center py-8 text-gray-400">Aucun utilisateur</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Main tenant list ---
  return (
    <div className="min-h-screen bg-gray-50" data-testid="super-admin-dashboard">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Super Admin</h1>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onSwitchToTenantApp && (
              <button
                onClick={onSwitchToTenantApp}
                className="px-4 py-2 text-sm bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors font-medium flex items-center gap-1.5"
                data-testid="switch-to-tenant-app-btn"
              >
                Mon espace M&M →
              </button>
            )}
            <button onClick={logout} className="px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-testid="super-admin-logout">Deconnexion</button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={<Building2 className="h-5 w-5" />} label="Conciergeries" value={activeTenants.length} color="indigo" testid="stat-active-tenants" />
          <StatCard icon={<Users className="h-5 w-5" />} label="Utilisateurs" value={totalStats.users} color="blue" testid="stat-users" />
          <StatCard icon={<Home className="h-5 w-5" />} label="Logements" value={totalStats.housings} color="green" testid="stat-housings" />
          <StatCard icon={<Ban className="h-5 w-5" />} label="Suspendus" value={suspendedCount} color="red" testid="stat-suspended" />
          <StatCard icon={<AlertTriangle className="h-5 w-5" />} label="Alertes" value={alertCount} color="amber" testid="stat-alerts" />
          <StatCard
            icon={<Trash2 className="h-5 w-5" />}
            label={showOnlyInactive ? "Désactivés (vue active)" : "Désactivés"}
            value={inactiveCount}
            color="gray"
            active={showOnlyInactive}
            onClick={() => {
              const next = !showOnlyInactive;
              setShowOnlyInactive(next);
              if (next && !includeInactive) setIncludeInactive(true);
            }}
            testid="stat-inactive-tenants"
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-gray-900">Conciergeries</h2>
            <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                data-testid="toggle-include-inactive"
              />
              Afficher les désactivés
            </label>
          </div>
          <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors" data-testid="create-tenant-btn">
            <Plus className="h-4 w-4" /> Nouvelle conciergerie
          </button>
        </div>

        {/* Tenant list */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
        ) : filteredTenants.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {showOnlyInactive ? "Aucune conciergerie désactivée" : "Aucune conciergerie creee"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTenants.map((tenant) => {
              const isExpiringSoon = tenant.days_until_expiration !== null && tenant.days_until_expiration <= 7 && tenant.days_until_expiration >= 0 && !tenant.suspended;
              const borderColor = tenant.suspended ? "border-red-300 bg-red-50/40" : isExpiringSoon ? "border-amber-300 bg-amber-50/30" : !tenant.active ? "border-gray-300 bg-gray-50 opacity-60" : "border-gray-200";

              return (
                <div key={tenant.id} className={`bg-white rounded-xl border p-4 transition-all ${borderColor}`} data-testid={`tenant-card-${tenant.id}`}>
                  {/* Top row: name + badges */}
                  <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => openTenantUsers(tenant)}>
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0 ${tenant.suspended ? "bg-red-500" : tenant.active ? "bg-indigo-600" : "bg-gray-400"}`}>
                        {tenant.company_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900">{tenant.company_name}</p>
                          {isExpiringSoon && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Expire dans {tenant.days_until_expiration}j</span>}
                          {tenant.expired && tenant.suspended && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Expire</span>}
                        </div>
                        <p className="text-sm text-gray-500">{tenant.admin_email}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                          <span><Users className="h-3 w-3 inline mr-0.5" />{tenant.stats?.users || 0}</span>
                          <span><Home className="h-3 w-3 inline mr-0.5" />{tenant.stats?.housings || 0}</span>
                          <span><Calendar className="h-3 w-3 inline mr-0.5" />{tenant.stats?.missions || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom row: action buttons */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 flex-wrap">
                    {!tenant.active ? (
                      // Tenant désactivé : un seul bouton "Réactiver"
                      <>
                        <button onClick={(e) => { e.stopPropagation(); toggleTenant(tenant); }}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-100 text-green-700 hover:bg-green-200 transition-colors inline-flex items-center gap-1"
                          data-testid={`reactivate-tenant-${tenant.id}`}>
                          <UserCheck className="h-3 w-3" /> Réactiver
                        </button>
                        <span className="text-xs text-gray-500 italic ml-auto">Désactivé</span>
                      </>
                    ) : (
                      <>
                        {/* Access button - Basculer vers ce tenant */}
                        <button onClick={(e) => { e.stopPropagation(); switchToTenant(tenant); }}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors inline-flex items-center gap-1"
                          data-testid={`access-tenant-${tenant.id}`}>
                          <LogIn className="h-3 w-3" /> Accéder
                        </button>

                        {/* Suspend / Reactivate button */}
                        <button onClick={(e) => { e.stopPropagation(); toggleSuspend(tenant); }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors inline-flex items-center gap-1 ${tenant.suspended ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-orange-100 text-orange-700 hover:bg-orange-200"}`}
                          data-testid={`suspend-tenant-${tenant.id}`}>
                          {tenant.suspended ? <><UserCheck className="h-3 w-3" /> Reactiver</> : <><Ban className="h-3 w-3" /> Suspendre</>}
                        </button>

                        {/* Edit button */}
                        <button onClick={(e) => { e.stopPropagation(); openEditTenant(tenant); }}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors inline-flex items-center gap-1"
                          data-testid={`edit-tenant-${tenant.id}`}>
                          <Pencil className="h-3 w-3" /> Modifier
                        </button>

                        {/* Deactivate button (soft-delete) */}
                        <button onClick={(e) => { e.stopPropagation(); deleteTenant(tenant); }}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition-colors inline-flex items-center gap-1"
                          data-testid={`delete-tenant-${tenant.id}`}>
                          <Trash2 className="h-3 w-3" /> Désactiver
                        </button>
                      </>
                    )}

                    {/* Expiration buttons */}
                    {editingExpiration === tenant.id ? (
                      <div className="flex items-center gap-2">
                        <input type="date" value={expirationInput} onChange={(e) => setExpirationInput(e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" data-testid={`expiration-input-${tenant.id}`} />
                        <button onClick={() => saveExpiration(tenant.id)} className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-xs font-semibold hover:bg-indigo-200" data-testid={`save-expiration-${tenant.id}`}>Valider</button>
                        <button onClick={() => setEditingExpiration(null)} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200">Annuler</button>
                      </div>
                    ) : (
                      <>
                        {tenant.expiration_date ? (
                          <>
                            <button onClick={() => { setEditingExpiration(tenant.id); setExpirationInput(tenant.expiration_date.split("T")[0]); }}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${isExpiringSoon ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}
                              data-testid={`edit-expiration-${tenant.id}`}>
                              Expire le {new Date(tenant.expiration_date).toLocaleDateString("fr-FR")}
                            </button>
                            <button onClick={() => { setExpirationInput(""); saveExpiration(tenant.id); }}
                              className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition-colors"
                              data-testid={`set-unlimited-${tenant.id}`}>
                              Illimite
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold">Illimite</span>
                            <button onClick={() => { setEditingExpiration(tenant.id); setExpirationInput(""); }}
                              className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors"
                              data-testid={`edit-expiration-${tenant.id}`}>
                              Definir une date
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Credentials confirmation modal after creation */}
      {createdCredentials && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-lg font-bold">Conciergerie creee !</h2>
              <p className="text-sm text-gray-500 mt-1">{createdCredentials.company_name}</p>
            </div>
            <div className="space-y-3 bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase">Identifiants de connexion</p>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-mono text-gray-900 truncate" data-testid="created-email">{createdCredentials.email}</p>
                </div>
                <button onClick={() => copyToClipboard(createdCredentials.email, 'email')}
                  className="shrink-0 p-1.5 rounded-md hover:bg-gray-200 transition-colors" data-testid="copy-email-btn">
                  {copiedField === 'email' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500">Mot de passe</p>
                  <p className="text-sm font-mono text-gray-900" data-testid="created-password">{createdCredentials.password}</p>
                </div>
                <button onClick={() => copyToClipboard(createdCredentials.password, 'password')}
                  className="shrink-0 p-1.5 rounded-md hover:bg-gray-200 transition-colors" data-testid="copy-password-btn">
                  {copiedField === 'password' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4 text-gray-400" />}
                </button>
              </div>
              <div className="pt-1">
                <button onClick={() => { const text = `Lien: ${LOGIN_URL}\nEmail: ${createdCredentials.email}\nMot de passe: ${createdCredentials.password}`; copyToClipboard(text, 'all'); }}
                  className="w-full py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors" data-testid="copy-all-btn">
                  {copiedField === 'all' ? 'Copie !' : 'Copier tout'}
                </button>
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-3 text-center">Transmettez ces identifiants au gerant de la conciergerie.</p>
            <div className="mt-3 bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Lien de connexion</p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-mono text-blue-700 truncate" data-testid="created-login-url">{LOGIN_URL}</p>
                <button onClick={() => copyToClipboard(LOGIN_URL, 'url')}
                  className="shrink-0 p-1.5 rounded-md hover:bg-blue-100 transition-colors" data-testid="copy-url-btn">
                  {copiedField === 'url' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4 text-blue-400" />}
                </button>
              </div>
            </div>
            <div className="flex justify-center mt-4">
              <button onClick={() => setCreatedCredentials(null)} className="px-6 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800" data-testid="close-credentials-btn">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit tenant modal */}
      {editingTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-lg font-bold mb-4">Modifier — {editingTenant.company_name}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lien de connexion</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-blue-50 text-blue-700 font-mono truncate">{LOGIN_URL}</div>
                  <button onClick={() => copyToClipboard(LOGIN_URL, 'edit-url')} className="shrink-0 p-2 rounded-lg hover:bg-gray-100" data-testid="edit-copy-url">
                    {copiedField === 'edit-url' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email administrateur</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700 select-all" data-testid="edit-tenant-email">{editingTenant.admin_email}</div>
                  <button onClick={() => copyToClipboard(editingTenant.admin_email, 'edit-email')} className="shrink-0 p-2 rounded-lg hover:bg-gray-100" data-testid="edit-copy-email">
                    {copiedField === 'edit-email' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
                <div className="relative">
                  <input type={showEditPassword ? "text" : "password"} value={editPassword} onChange={(e) => { setEditPassword(e.target.value); setPasswordSaved(false); }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-10"
                    placeholder="Entrez un nouveau mot de passe" data-testid="edit-tenant-password" />
                  <button type="button" onClick={() => setShowEditPassword(!showEditPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showEditPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {passwordSaved && <p className="text-xs text-green-600 mt-1">Mot de passe mis a jour avec succes</p>}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setEditingTenant(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Fermer</button>
                <button onClick={saveAdminPassword} disabled={!editPassword || editPassword.length < 6}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="save-tenant-password">
                  Enregistrer le mot de passe
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-lg font-bold mb-4">Nouvelle conciergerie</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la conciergerie</label>
                <input type="text" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Ex: Clean & Co Marseille" data-testid="tenant-company-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'administrateur</label>
                <input type="text" required value={form.admin_full_name} onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Jean Dupont" data-testid="tenant-admin-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email administrateur</label>
                <input type="email" required value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="admin@conciergerie.fr" data-testid="tenant-admin-email-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} required value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-10" placeholder="Mot de passe" data-testid="tenant-admin-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); setShowPassword(false); }} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" data-testid="submit-create-tenant">Creer la conciergerie</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, value, color, onClick, active = false, testid }) => {
  const colorMap = {
    indigo: "bg-indigo-50 text-indigo-600",
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    gray: "bg-gray-100 text-gray-600",
  };
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`text-left bg-white rounded-xl border p-3 w-full transition-all ${onClick ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : ''} ${active ? 'ring-2 ring-indigo-500 border-indigo-500' : 'border-gray-200'}`}
    >
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center mb-2 ${colorMap[color]}`}>{icon}</div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </Tag>
  );
};

export default SuperAdminDashboard;
